<script setup lang="ts">
import { Inbox } from "@lucide/vue";
import type { Component } from "vue";
import { cn } from "@/lib/utils";

/**
 * "Nothing here — and here's what to do next."
 * Props: title, description, icon. `compact` is the inset form for use inside
 * cards and panels (no border, quiet sunken fill, less padding); without it
 * the dashed page-level form. Default slot: next-step buttons.
 */
const props = defineProps<{ title: string; description?: string; icon?: Component; compact?: boolean; class?: string }>();
</script>

<template>
  <div
    :class="
      cn(
        'flex flex-col items-center justify-center gap-1.5 text-center',
        compact ? 'surface-inset px-4 py-6' : 'rounded-xl border border-dashed border-border-strong px-6 py-12',
        props.class,
      )
    "
  >
    <div
      :class="
        cn(
          'mb-1.5 grid place-items-center text-muted-foreground',
          compact ? 'size-8 rounded-lg bg-card shadow-xs ring-1 ring-border' : 'size-11 rounded-xl bg-card shadow-sm ring-1 ring-border',
        )
      "
    >
      <component :is="icon ?? Inbox" :class="compact ? 'size-4' : 'size-5'" aria-hidden="true" />
    </div>
    <p class="text-sm font-semibold text-balance">{{ title }}</p>
    <p v-if="description" class="max-w-md text-sm text-pretty text-muted-foreground">{{ description }}</p>
    <div v-if="$slots.default" class="mt-2.5 flex flex-wrap justify-center gap-2"><slot /></div>
  </div>
</template>
